import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { translations } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send cookies on cross-origin requests (admin JWT session)
axios.defaults.withCredentials = true;

// Resolve uploaded/static asset URLs (backend) vs frontend public files
export const assetUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith("/api/")) return `${BACKEND_URL}${path}`;
  return path; // served by frontend (/logo.png etc.)
};

// Filigranes des photos produits, retirés par recadrage à l'affichage (les originaux ne sont pas modifiés) :
//  - images « studio » générées par IA : repère « Ai » en haut à gauche -> recadrage central à 84 % (comme les images principales)
//  - photos prises au téléphone : horodatage en bas à droite -> on garde les 94 % du haut
const watermarkCrop = (path) => {
  const f = String(path).split("/").pop();
  if (/^(studio_|Studio_studio|E-commerce_Studio)/i.test(f)) return "c_crop,g_center,w_0.84,h_0.84";
  if (/^(HEAD_wetransfer_)?1000\d{6}_/.test(f)) return "c_crop,g_north,h_0.94,w_1.0";
  return "";
};

// Domaine « CDN » optionnel (réglage CMS `image_cdn_base`, ex. https://img.dz-tennisshopper.store) : un Worker Cloudflare mémorise les images
// pour ne pas consommer la bande passante Cloudinary à chaque visite. Vide = adresses Cloudinary directes (comportement d'origine).
let _imgBase = "";
export const setImageCdnBase = (b) => { _imgBase = String(b || "").trim().replace(/\/+$/, ""); };
const withCdn = (u) => (_imgBase && typeof u === "string" && u.startsWith("https://res.cloudinary.com/") ? `${_imgBase}/${u.slice("https://res.cloudinary.com/".length)}` : u);

// Inject Cloudinary delivery transformations (f_auto,q_auto + optional w/h/dpr)
// Pass-through for any non-Cloudinary URL so callers can use it everywhere safely.
const cldUrlRaw = (url, opts = {}) => {
  const u = assetUrl(url);
  if (!u || !u.includes("res.cloudinary.com") || !u.includes("/upload/")) return u;
  const idx = u.indexOf("/upload/") + "/upload/".length;
  const head = u.slice(0, idx);
  const tail = u.slice(idx);
  // Une transformation est déjà dans l'adresse (ex. recadrage « c_crop,g_center,w_0.84,h_0.84/ » des images produits) : on la garde
  // et on ajoute NOTRE optimisation juste après (Cloudinary enchaîne les segments séparés par « / »). Avant, on renvoyait l'adresse
  // telle quelle : le PNG d'origine (3 à 4,5 Mo) était servi à chaque visiteur, ce qui a épuisé le quota de bande passante.
  const pre = tail.match(/^((?:[a-z]{1,3}_[^/]*\/)+)(v\d+\/.+)$/i);
  if (!pre && /^[a-z]{1,3}_[a-z0-9]/i.test(tail) && !/^v\d+/.test(tail)) return u; // forme inattendue : on ne touche à rien
  if (pre && /(^|[,/])f_/.test(pre[1])) return u; // déjà un format choisi : on respecte
  if (pre && /e_gen_/.test(pre[1])) return u; // retouche par IA (e_gen_remove...) : chaque nouvelle variante coûterait des crédits
  // petites images (cartes, vignettes) : q_auto:eco, environ 13 % plus léger à l'œil identique ; grandes images : q_auto
  const parts = [`f_${opts.f || "auto"}`, `q_${opts.q || (opts.w && opts.w <= 800 ? "auto:eco" : "auto")}`];
  if (opts.w) parts.push(`w_${opts.w}`);
  if (opts.h) parts.push(`h_${opts.h}`);
  if (opts.c) parts.push(`c_${opts.c}`);
  if (opts.dpr) parts.push(`dpr_${opts.dpr}`);
  // sans largeur demandée, on borne quand même à 1200 px (jamais le plein format 2048-4096 px)
  if (!opts.w && !opts.h) parts.push("w_1200", "c_limit");
  // recadrage anti-filigrane, seulement si l'adresse n'a pas déjà un recadrage
  const wm = !pre || !/c_crop/.test(pre[1]) ? watermarkCrop(pre ? pre[2] : tail) : "";
  return `${head}${wm ? `${wm}/` : ""}${pre ? pre[1] : ""}${parts.join(",")}/${pre ? pre[2] : tail}`;
};
export const cldUrl = (url, opts = {}) => withCdn(cldUrlRaw(url, opts));

// Build a srcset string from widths e.g. [320, 640, 1024]
export const cldSrcSet = (url, widths = [320, 640, 1024]) => {
  const u = assetUrl(url);
  if (!u || !u.includes("res.cloudinary.com")) return "";
  return widths.map((w) => `${cldUrl(url, { w })} ${w}w`).join(", ");
};

const LanguageCtx = createContext(null);
const CartCtx = createContext(null);
const SettingsCtx = createContext(null);

// ---------- Language ----------
export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || ((navigator.language || "").toLowerCase().startsWith("ar") ? "ar" : "fr"));
  const t = translations[lang];
  useEffect(() => {
    document.documentElement.dir = t.dir;
    document.documentElement.lang = lang;
    localStorage.setItem("lang", lang);
    // Notify SettingsProvider (and any other listener) so meta/title/JSON-LD re-render for the new language
    try { window.dispatchEvent(new CustomEvent("dzts-lang-change", { detail: { lang } })); } catch { /* ignore */ }
  }, [lang, t.dir]);
  const toggle = () => setLang((l) => (l === "fr" ? "ar" : "fr"));
  return <LanguageCtx.Provider value={{ lang, t, setLang, toggle }}>{children}</LanguageCtx.Provider>;
}
export const useLang = () => useContext(LanguageCtx);

// ---------- Settings + Preview ----------
const DEFAULTS = {
  site_name: "DZ Tennis Shopper",
  logo_url: "/logo.png",
  theme: "clay",
  primary_color: "#DFFF00",
  secondary_color: "#E04F33",
  bg_color: "#0A0A0A",
  font_heading: "Changa",
  font_body: "Cairo",
  exchange_rate: 280,
  show_dzd: true,
  free_shipping_threshold_dzd: 10000,
  delivery_fee_dzd: 500,
  delivery_fee_stopdesk_dzd: 250,
  free_shipping_threshold_stopdesk_dzd: 10000,
  free_shipping_threshold_home_dzd: 20000,
  whatsapp_fr: "+33758399751",
  whatsapp_dz: "+213661976005",
  payment_methods: [],
  carriers: [],
  trust_cards: [],
  product_categories: ["racket", "string", "bag", "shoe", "accessory"],
  product_category_labels_fr: { racket: "Raquettes", string: "Cordages", bag: "Sacs", shoe: "Chaussures", accessory: "Accessoires" },
  product_category_labels_ar: { racket: "مضارب", string: "أوتار", bag: "حقائب", shoe: "أحذية", accessory: "إكسسوارات" },
  about_image_urls: [],
  promo_bar_items_fr: [],
  promo_bar_items_ar: [],
  hero_enabled: true, about_enabled: true, trust_enabled: true, dz_shopper_enabled: true,
  footer_enabled: true, promo_bar_enabled: true, fomo_enabled: true, hero_stats_enabled: true,
  footer_show_contacts: true, footer_show_carriers: true, footer_show_payments: true,
  fomo_delay_seconds: 35,
};

export function SettingsProvider({ children }) {
  const [saved, setSaved] = useState(DEFAULTS);
  const [preview, setPreview] = useState(null); // null or partial overrides
  const [langTick, setLangTick] = useState(0); // bumps on lang change to re-run meta effect
  const settings = useMemo(() => ({ ...saved, ...(preview || {}) }), [saved, preview]);
  setImageCdnBase(settings?.image_cdn_base); // synchrone, avant le rendu des images (idempotent)

  // Re-run SEO/meta effect whenever LanguageProvider toggles the language
  useEffect(() => {
    const handler = () => setLangTick((n) => n + 1);
    window.addEventListener("dzts-lang-change", handler);
    return () => window.removeEventListener("dzts-lang-change", handler);
  }, []);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSaved({ ...DEFAULTS, ...data });
    } catch (e) {
      console.error("settings load error", e);
    }
  };
  useEffect(() => { refresh(); }, []);

  // Sync DZD rounding step used by dzdRound()/fmtDzd() with the CMS setting.
  // Priority: round_dzd_step (if defined) → legacy round_dzd_to_nearest_100 → default 50.
  useEffect(() => {
    const raw = settings?.round_dzd_step;
    if (raw !== undefined && raw !== null && raw !== "") {
      setDzdStep(Number(raw));
    } else {
      setDzdStep(settings?.round_dzd_to_nearest_100 ? 100 : 50);
    }
  }, [settings?.round_dzd_step, settings?.round_dzd_to_nearest_100]);

  // Noindex conditionnel — uniquement sur l'host de test Emergent, jamais sur le domaine principal.
  // Isolé du reste du SEO (aucune dépendance partagée, ne s'exécute qu'au montage).
  useEffect(() => {
    if (window.location.hostname.toLowerCase() !== "tennis-dz-shop-r-1778527021.emergent.host") return;
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(meta);
  }, []);

  // Apply theme + CSS variables + SEO/meta + analytics scripts
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--c-primary", settings.primary_color);
    root.style.setProperty("--c-secondary", settings.secondary_color);
    root.style.setProperty("--c-bg", settings.bg_color);
    document.body.dataset.theme = settings.theme || "clay";
    // Detect current language for i18n-aware title/description
    const currentLang = document.documentElement.lang || localStorage.getItem("lang") || "fr";
    const isAr = currentLang === "ar";
    // Meta title (only override on the home page; product pages set their own title)
    const isProductPage = /^\/produit\//.test(window.location.pathname || "");
    if (!isProductPage) {
      const frTitle = settings.meta_title || `${settings.site_name || "DZ Tennis Shop"} — Raquettes HEAD & Pro's Pro en Algérie`;
      const arTitle = settings.meta_title_ar
        || `${settings.site_name || "DZ Tennis Shop"} — مضارب HEAD و Pro's Pro في الجزائر`;
      document.title = isAr ? arTitle : frTitle;
    }
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    const frDesc = settings.meta_description || "DZ Tennis Shop — Importation HEAD & Pro's Pro depuis la France vers l'Algérie. Livraison 58 wilayas, paiement à la livraison ou CIB.";
    const arDesc = settings.meta_description_ar || "متجر DZ Tennis — استيراد HEAD و Pro's Pro من فرنسا إلى الجزائر. توصيل إلى 58 ولاية، دفع عند الاستلام أو CIB.";
    metaDesc.setAttribute("content", isAr ? arDesc : frDesc);
    // Open Graph (social share) — home only
    if (!isProductPage) {
      const setMeta = (prop, value, attr = "property") => {
        if (!value) return;
        let m = document.querySelector(`meta[${attr}="${prop}"]`);
        if (!m) { m = document.createElement("meta"); m.setAttribute(attr, prop); document.head.appendChild(m); }
        m.setAttribute("content", value);
      };
      const ogImage = settings.og_image_url || settings.logo_url;
      setMeta("og:type", "website");
      setMeta("og:title", document.title);
      setMeta("og:description", isAr ? arDesc : frDesc);
      setMeta("og:url", window.location.origin + "/");
      setMeta("og:site_name", settings.site_name || "DZ Tennis Shop");
      if (ogImage) setMeta("og:image", ogImage.startsWith("http") ? ogImage : `${BACKEND_URL}${ogImage}`);
      setMeta("twitter:card", "summary_large_image", "name");
      setMeta("twitter:title", document.title, "name");
      setMeta("twitter:description", isAr ? arDesc : frDesc, "name");
      if (ogImage) setMeta("twitter:image", ogImage.startsWith("http") ? ogImage : `${BACKEND_URL}${ogImage}`, "name");
      // Canonical (home)
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
      canon.setAttribute("href", window.location.origin + "/");
      // JSON-LD Organization + WebSite (home)
      const injectLd = (id, payload) => {
        let s = document.getElementById(id);
        if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = id; document.head.appendChild(s); }
        s.textContent = JSON.stringify(payload);
      };
      injectLd("ld-organization", {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: settings.site_name || "DZ Tennis Shop",
        url: window.location.origin + "/",
        logo: settings.logo_url ? (settings.logo_url.startsWith("http") ? settings.logo_url : `${BACKEND_URL}${settings.logo_url}`) : undefined,
        sameAs: [settings.facebook_url, settings.instagram_url, settings.tiktok_url].filter(Boolean),
        contactPoint: [{
          "@type": "ContactPoint",
          contactType: "customer service",
          telephone: settings.whatsapp_dz || "+213661976005",
          areaServed: "DZ",
          availableLanguage: ["French", "Arabic"],
        }],
      });
      injectLd("ld-website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: settings.site_name || "DZ Tennis Shop",
        url: window.location.origin + "/",
        inLanguage: [isAr ? "ar-DZ" : "fr-DZ"],
      });
    }
    // Favicon (browser tab)
    const faviconUrl = settings.favicon_url ? (settings.favicon_url.startsWith("http") ? settings.favicon_url : `${BACKEND_URL}${settings.favicon_url}`) : null;
    if (faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }
  }, [settings.primary_color, settings.secondary_color, settings.bg_color, settings.theme, settings.meta_title, settings.meta_title_ar, settings.meta_description, settings.meta_description_ar, settings.favicon_url, settings.logo_url, settings.og_image_url, settings.site_name, settings.whatsapp_dz, settings.facebook_url, settings.instagram_url, settings.tiktok_url, langTick]);

  // Google Tag Manager (GTM) — script in <head> + noscript iframe in <body>
  // GTM is a container that can host GA, Ads conversion, custom HTML, etc.
  // It only fires once per page-load (uses dataLayer; safe to coexist with the
  // direct GA snippet below if the admin wants both).
  useEffect(() => {
    const id = (settings.gtm_id || "").trim();
    if (!id) return;
    if (document.getElementById("gtm-tag")) return;
    // Validate format (GTM-XXXXXXX) to avoid injecting anything weird
    if (!/^GTM-[A-Z0-9]+$/i.test(id)) return;
    // 1) <head> script
    const s = document.createElement("script");
    s.id = "gtm-tag";
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;
    document.head.appendChild(s);
    // 2) <noscript> iframe fallback right after <body>
    if (!document.getElementById("gtm-noscript")) {
      const ns = document.createElement("noscript");
      ns.id = "gtm-noscript";
      ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(ns, document.body.firstChild);
    }
  }, [settings.gtm_id]);

  // Google Analytics
  useEffect(() => {
    if (!settings.ga_id) return;
    if (document.getElementById("ga-tag")) return;
    const s1 = document.createElement("script");
    s1.id = "ga-tag";
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${settings.ga_id}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${settings.ga_id}');`;
    document.head.appendChild(s2);
  }, [settings.ga_id]);

  // Facebook Pixel — DISABLED here: TrackingScripts.jsx already handles Meta Pixel
  // injection (via settings.meta_pixel_id) with a global __fbq_loaded__ guard.
  // Keeping this second init caused every fbq('track', ...) event to fire twice
  // in Meta Events Manager (duplicate Purchase / ViewContent / AddToCart / InitiateCheckout).

  // TikTok Pixel
  useEffect(() => {
    if (!settings.tiktok_pixel_id) return;
    if (document.getElementById("tt-pixel")) return;
    const s = document.createElement("script");
    s.id = "tt-pixel";
    s.innerHTML = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.load=function(e){var i="https://analytics.tiktok.com/i18n/pixel/events.js",n=d.createElement("script");n.type="text/javascript",n.async=!0,n.src=i+"?sdkid="+e;var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(n,a)};ttq.load('${settings.tiktok_pixel_id}');ttq.page();}(window,document,'ttq');`;
    document.head.appendChild(s);
  }, [settings.tiktok_pixel_id]);

  return (
    <SettingsCtx.Provider value={{ settings, saved, refresh, setSaved, preview, setPreview }}>
      {children}
    </SettingsCtx.Provider>
  );
}
export const useSettings = () => useContext(SettingsCtx);

// ---------- Cart ----------
export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cart") || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  useEffect(() => { localStorage.setItem("cart", JSON.stringify(items)); }, [items]);

  // Returns the per-variant or per-product stock limit. null = unlimited (no count set).
  const _maxFor = (item) => {
    if (item?.variant_stock_count != null) return Number(item.variant_stock_count);
    if (item?.stock_count != null) return Number(item.stock_count);
    return null;
  };

  // Build a stable cart key per product + variant combination so different
  // variants of the same product stay as separate cart lines.
  const _cartKey = (item) => {
    const so = item?._selected_options;
    if (so && typeof so === "object" && Object.keys(so).length) {
      const sorted = Object.keys(so).sort().map((k) => `${k}=${so[k]}`).join("|");
      return `${item.id}::${sorted}`;
    }
    return String(item.id);
  };

  const add = (product, qty = 1, openCart = true) => {
    let capped = false;
    const key = _cartKey(product);
    setItems((prev) => {
      const found = prev.find((p) => _cartKey(p) === key);
      const max = _maxFor(product);
      if (found) {
        const target = found.qty + qty;
        const finalQty = max != null ? Math.min(target, max) : target;
        if (max != null && finalQty < target) capped = true;
        return prev.map((p) => (_cartKey(p) === key ? { ...p, qty: finalQty } : p));
      }
      const finalQty = max != null ? Math.min(qty, max) : qty;
      if (max != null && finalQty < qty) capped = true;
      return [...prev, { ...product, qty: finalQty, cart_key: key }];
    });
    if (capped) {
      try {
        const m = product.name || "ce produit";
        const max = _maxFor(product);
        console.warn(`Stock max atteint pour ${m} : ${max}`);
      } catch { /* ignore */ }
    }
    if (openCart) setOpen(true);
    return { capped };
  };
  const remove = (cartKey) => setItems((prev) => prev.filter((p) => (p.cart_key || _cartKey(p)) !== cartKey));
  const update = (cartKey, qty) => setItems((prev) => prev.map((p) => {
    if ((p.cart_key || _cartKey(p)) !== cartKey) return p;
    const max = _maxFor(p);
    const target = Math.max(1, qty);
    const finalQty = max != null ? Math.min(target, max) : target;
    return { ...p, qty: finalQty };
  }));
  const clear = () => setItems([]);
  const count = items.reduce((s, p) => s + p.qty, 0);
  const totalEur = items.reduce((s, p) => s + p.price_eur * p.qty, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, update, clear, count, totalEur, open, setOpen }}>
      {children}
    </CartCtx.Provider>
  );
}
export const useCart = () => useContext(CartCtx);

export const fmtEur = (n) => `${n.toFixed(2)} €`;
// Configurable DZD rounding step — set by SettingsProvider from `settings.round_dzd_step`.
// Values: 0 = no rounding, 50 or 100. Default 50 (customer preference). Legacy
// `round_dzd_to_nearest_100=true` falls back to 100 for backward compatibility.
let __DZD_STEP = 50;
export const setDzdStep = (s) => {
  const n = Number(s);
  __DZD_STEP = [0, 50, 100].includes(n) ? n : 50;
};
export const dzdRound = (n) => {
  const v = Number(n) || 0;
  const s = __DZD_STEP;
  if (!s || s <= 0) return Math.round(v);
  return Math.round(v / s) * s;
};
export const fmtDzd = (n) => `${dzdRound(n).toLocaleString("fr-FR")} DZD`;
// Unified product-level DZD price. Uses `price_dzd` when set (>0), otherwise
// converts EUR × exchange_rate. Rounding step honours the CMS setting.
export const productPriceDzd = (product, settings) => {
  const rate = settings?.exchange_rate || 280;
  const raw = product?.price_dzd > 0 ? product.price_dzd : (product?.price_eur || 0) * rate;
  return dzdRound(raw);
};
